import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { progressVariants } from "@/lib/progress-variants";

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> &
    VariantProps<typeof progressVariants> & {
      showValue?: boolean;
      title?: string;
      required?: boolean;
      percentageDisplay?: "none" | "preview" | "badge" | "tooltip";
    }
>(
  (
    {
      className,
      variant,
      size,
      position,
      percentageDisplay,
      value,
      showValue = false,
      title,
      required = false,
      ...props
    },
    ref
  ) => {
    const isComplete = value === 100;
    const isLoading = value === undefined || value === null;
    const percentage = value !== undefined && value !== null ? Math.round(value) : 0;

    return (
      <div className="wl-progress-wrapper">
        {title && (
          <div
            data-slot="progress-title"
            className={cn(
              "wl-progress-title",
              required && "wl-progress-title-require",
              className
            )}
          >
            {title}
            {showValue && value !== undefined && <span> {percentage}%</span>}
          </div>
        )}
        <ProgressPrimitive.Root
          ref={ref}
          data-slot="progress"
          className={cn(
            progressVariants({ variant, size, position, percentageDisplay }),
            className
          )}
          {...props}
        >
          {percentageDisplay === "tooltip" && (
            <div
              data-slot="progress-percentage"
              className="wl-progress-percentage"
              style={{
                left: `${Math.min(Math.max(percentage, 5), 95)}%`,
              }}
            >
              {percentage}%
            </div>
          )}
          <ProgressPrimitive.Indicator
            data-slot="progress-indicator"
            className={cn(
              "wl-progress-line",
              isLoading && "wl-loading",
              isComplete && "wl-complete"
            )}
            data-percentage={`${percentage}%`}
            style={{ transform: `translateX(-${100 - (value !== null && value !== undefined ? value : 0)}%)` }}
          />
        </ProgressPrimitive.Root>
      </div>
    );
  }
);

Progress.displayName = "Progress";

export { Progress, progressVariants };
